import { type ComponentProps, type ReactNode, useId } from 'react'

interface ChoiceProps extends Omit<ComponentProps<'input'>, 'className' | 'type' | 'children'> {
  type: 'radio' | 'checkbox'
  /** What is being chosen. */
  children: ReactNode
  /** A line beneath it, such as whose kits ship to an address. */
  description?: ReactNode
}

/**
 * A radio or checkbox with its label. Native, so the browser supplies the
 * keyboard and group behaviour inside the shadow root with no custom focus code.
 */
export function Choice({ type, children, description, id, ...props }: ChoiceProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId

  return (
    <label htmlFor={inputId} className="flex cursor-pointer items-start gap-3 py-1.5">
      <input
        {...props}
        id={inputId}
        type={type}
        className="mt-1 size-4 shrink-0 accent-brand outline-none focus-visible:ring-2 focus-visible:ring-brand-ring"
      />
      <span className="min-w-0">
        <span className="block text-base text-ink">{children}</span>
        {description && <span className="block text-sm text-ink-soft">{description}</span>}
      </span>
    </label>
  )
}
